import { Component, OnInit, ViewContainerRef } from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material";
import { DatasetActionContainerService } from "app/main/components/dataset/services/dataset-action-container.service";
import { DatasetNavigatorService } from "app/main/components/dataset/services/dataset-navigator.service";
import { DatasetRecordService } from "app/main/components/dataset/services/dataset-record.service";
import { DatasetService } from "app/main/components/dataset/services/dataset.service";
import { DestroyableComponent } from "app/main/destroyable.component";
import { BookingService } from "app/main/pages/edit-booking/booking.service";
import { AppService } from "app/services/app.service";
import { DomainFilterService } from "app/services/domain-filter.service";
import { EMPTY, Subject } from "rxjs";
import { TripBookingPackagesStepService } from "../../../trip-booking-packages-step.service";
import { TripBookingPackageService } from "../../trip-booking-package.service";
import { catchError, takeUntil, tap } from "rxjs/operators";
import { ValueTypes, InputTypes, IFieldDefinition } from "app/interfaces";
import { FormDialogComponent, IFormDialogData } from "app/main/dialogs/form-dialog/dialog.component";
import { ActionDialogComponent } from "app/main/pages/edit-booking/action-dialog/action-dialog.component";

@Component({
	selector   : 'trip-package-open-date-booking-block',
	templateUrl: './trip-package-open-date-booking-block.component.html',
	styleUrls: ['./trip-package-open-date-booking-block.component.scss'],
	providers: []
})
export class TripPackageOpenDateBookingBlockComponent extends DestroyableComponent implements OnInit{

	protected _unsubscribeAll: Subject<any>;

	public canResendEmail = false;
	public canConfirmDate = false;
	public tripBookingPackage = null;
	public statusMessage = null;

	constructor(
		public appService: AppService,
		public datasetRS: DatasetRecordService,
		public datasetACS: DatasetActionContainerService,
		protected datasetNavigatorService: DatasetNavigatorService,
		public datasetService: DatasetService,
		public matDialog: MatDialog,
		public domainFilterService: DomainFilterService,
		public tripBookingPackageService: TripBookingPackageService,
		public tripBookingPackagesStepService: TripBookingPackagesStepService,
		public bookingService: BookingService,
		public viewContainerRef: ViewContainerRef
	){
		super();
	}
	
	ngOnInit(): void{
		this.datasetRS.record
		.pipe(takeUntil(this._unsubscribeAll))
		.subscribe((record) => {
			this.tripBookingPackage = record;
			this.canResendEmail = this.tripBookingPackage.booking_status == 'optional' && ['require_customer_confirm', 'require_customer_payment'].includes(this.tripBookingPackage.booking_sub_status)
			this.canConfirmDate = this.tripBookingPackage.booking_status == 'optional' && this.tripBookingPackage.booking_sub_status == 'require_admin_confirm';
			this.statusMessage = this.makeStatusMessage();
		});
	}

	makeStatusMessage(){
		if(this.tripBookingPackage.booking_status != 'optional') return null;
		if(this.tripBookingPackage.booking_sub_status == 'require_customer_confirm') return 'In attesa di conferma dal cliente.';
		if(this.tripBookingPackage.booking_sub_status == 'require_customer_payment') return 'In attesa di pagamento dal cliente.';
		if(this.tripBookingPackage.booking_sub_status == 'require_admin_confirm') return 'Conferma la data proposta dal cliente.';
		return null;
	}

	execCommand(commandCode: string, data: any){
		this.tripBookingPackagesStepService.loading = true;
		const dialogRef = this.matDialog.open(ActionDialogComponent, {
			width: '550px',
			data: {
				title: 'Azione in corso'
			}
		});

		return this.datasetService.post('/dataset/trip_booking_packages/command/'+commandCode, data)
		.pipe(
			takeUntil(this._unsubscribeAll),
			tap((response) => {
				this.tripBookingPackagesStepService.loading = false;
				this.onActionResponse(response, dialogRef)
			}),
			catchError(errorResponse => {
				this.tripBookingPackagesStepService.loading = false;
				this.onErrorActionResponse(errorResponse, dialogRef);
				return EMPTY;
			})
		);
	}

	resendEmailToCustomer(){
		this.execCommand('send_open_date_email_to_customer', {
			trip_booking_package_id: this.tripBookingPackage.id,
		})
		.subscribe({
			next: response => {
				this.datasetRS.reload();
			}
		});
	}

	confirmDate(){
		this.execCommand('confirm_open_date', {
			trip_booking_package_id: this.tripBookingPackage.id,
		})
		.subscribe({
			next: response => {
				this.datasetRS.reload();
			}
		});
	}

	changeDate(){
		const formFields: IFieldDefinition[] = [
			{
				key: 'start_day',
				name: 'Giorno',
				valueType: ValueTypes.STRING,
				inputConfig: {
					type: InputTypes.DATE
				}
			},
			{
				key: 'start_time',
				name: 'Orario',
				valueType: ValueTypes.STRING,
				inputConfig: {
					type: InputTypes.TIME
				}
			}
		];
		if(this.tripBookingPackage.booking_status == 'optional'){
			formFields.push({
				key: 'send_email',
				name: 'Invia email',
				valueType: ValueTypes.NUMBER,
				inputConfig: {
					type: InputTypes.CHECKBOX
				},
				titleIcon: 'info',
				titleTooltip: 'Verra inviata l\'email con il link per confermare e completare il pagamento.'
			});
		}
		this.matDialog.open<FormDialogComponent, IFormDialogData, any>(FormDialogComponent, {
			data: {
				title: 'Cambia data',
				formData: {
					start_day: this.tripBookingPackage.actual_trip.start_day,
					start_time: this.tripBookingPackage.actual_trip.start_time,
					send_email: 1
				},
				formConfig: {
					fields: formFields
				}
			}
		}).afterClosed()
		.subscribe(formResult => {
			if(!formResult) return;
			this.execCommand('change_open_date', {
				trip_booking_package_id: this.tripBookingPackage.id,
				start_day: formResult.start_day,
				start_time: formResult.start_time,
				send_email: formResult.send_email
			})
			.subscribe({
				next: response => {
					this.datasetRS.reload();
				}
			});
		});
	}

	onActionResponse(response, dialogRef: MatDialogRef<ActionDialogComponent>): void{
		if(!dialogRef || !dialogRef.componentInstance){
			dialogRef = this.matDialog.open(ActionDialogComponent, {
				width: '550px',
				data: {}
			});
		}
		dialogRef.componentInstance.loading = false;
		if(response.success){
			dialogRef.componentInstance.resultStatus = 'success';
			dialogRef.componentInstance.setTitle('Completato');
		}else{
			dialogRef.componentInstance.resultStatus = 'error';
			dialogRef.componentInstance.setTitle('Errore')
		}
		if(response.message){
			dialogRef.componentInstance.setMessage(response.message);
		}
	}

	onErrorActionResponse(response, dialogRef: MatDialogRef<ActionDialogComponent>): void{
		if(!dialogRef || !dialogRef.componentInstance){
			dialogRef = this.matDialog.open(ActionDialogComponent, {
				width: '550px',
				data: {
					title: 'Azione in corso'
				}
			});
		}
		dialogRef.componentInstance.loading = false;
		dialogRef.componentInstance.resultStatus = 'error';
		dialogRef.componentInstance.setTitle('Errore');
		if(response.error && response.error.message){
			dialogRef.componentInstance.setMessage(response.error.message);
		}else{
			if(response.error) dialogRef.componentInstance.setMessage(response.error);
			else dialogRef.componentInstance.setMessage('Si è verificato un errore.');
		}
	}
}